import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { GenericService } from '../../generic.service';
import { TroubleshootingStep } from '../interface/troubleshoot-eht.interface';
import { defaultLanguage } from '../../shared/constants/language.constants';
import { LandingService } from '../../landing-page/service/landing.service';

@Injectable({
  providedIn: 'root',
})
export class TroubleShootEltService {
  stepsList: any;
  issueName: string | undefined = '';
  currentLanguageName: string = defaultLanguage;

  constructor(
    private genericService: GenericService,
    private landingService: LandingService,
  ) {
    try {
      const stored = localStorage.getItem('deviceData');
      const selected = stored ? JSON.parse(stored).languageSelected : null;
      this.currentLanguageName = selected || defaultLanguage;
    } catch {
      this.currentLanguageName = defaultLanguage;
    }

    this.landingService.languageName$.subscribe((langName) => {
      this.currentLanguageName = langName || defaultLanguage;
    });
  }

  getTroubleShootEltIssues(): any[] {
    let issues: any[] = [];
    this.stepsList = [];

    for (const option of this.elt.options) {
      if (option.key === 'elt-machine-is-down') {
        if (option.steps && option.steps.length > 1) {
          const machineDownSteps = option.steps.map((step) => {
            return { key: step.key, label: option.label };
          });

          issues.push({ key: machineDownSteps[0].key, label: option.label });
          this.stepsList.push(...machineDownSteps);
        } else {
          issues.push({ key: option.key, label: option.label });
        }
      } else {
        if (option.steps && option.steps.length > 0) {
          const otherIssueStepKey = option.steps[0].key;
          issues.push({ key: otherIssueStepKey, label: option.label });
          this.stepsList.push(...option.steps);
        } else {
          issues.push({ key: option.key, label: option.label });
        }
      }
    }

    return issues;
  }

  setIssuename(issuename: string | undefined) {
    this.issueName = issuename;
  }

  getIssuename() {
    return this.issueName;
  }

  getTroubleShootStep(searchKey: string): TroubleshootingStep | null {
    return this.genericService.findObjectByKey(searchKey, this.elt.options);
  }

  getStepState() {
    return this.stepsList;
  }

  getStepIndex(key: string) {
    for (let i = 0; i < this.stepsList.length; i++) {
      if (this.stepsList[i].key === key) {
        return i;
      }
    }
    return -1; // If the key is not found
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   *
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a HeroService message with the MessageService */
  private log(message: string) {}

  elt: TroubleshootingStep = {
    key: 'elt',
    options: [
      {
        key: 'elt-machine-is-down',
        label: [
          'Machine is down',
          'Máquina no operativa',
          'La machine est en panne',
          'Machine is buiten bedrijf',
          'Macchina fuori servizio',
          '機械が停止している',
          'Máquina fora de serviço',
          'Maschine außer Betrieb',
        ],
        option: 'troubleshooting.elt.machineDown',
        steps: [
          {
            key: 'elt-filling-with-water',
            question: 'troubleshooting.es2000.machineDownFilling',
            label: [
              'Is the machine filling with water?',
              '¿La máquina esta llenandose con agua?',
              'La machine se remplit d’eau ?',
              'Wordt de machine gevuld met water?',
              'La macchina si sta riempiendo di acqua?',
              '機械に給水されていますか？',
              'A máquina está enchendo com água?',
              'Füllt sich die Maschine mit Wasser?',
            ],
            optionsLabel: '',
            description: ['', '', '', '', '', '', '', ''],
            images: [
              {
                url: '',
                alt: '',
              },
            ],
            options: [
              {
                key: 'elt-yes',
                option: 'troubleshooting.yes',
                label: ['Yes', 'Si', 'Oui', 'Ja', 'Sì', 'はい', 'Sim', 'Ja'],
                icon: 'check',
                steps: [
                  {
                    key: 'elt-machinelist-items',
                    question: 'Checklist Items',
                    options: [],
                    machineIssuelistItems: [
                      {
                        title: {
                          english: 'incoming water temperatures exceed 120°F',
                          spanish:
                            'Las temperaturas del agua entrante superan los 120 °F.',
                          french:
                            'Les températures d’eau entrantes dépassent 120°F.',
                          dutch:
                            'de temperaturen van het binnenkomende water overschrijden 120°F',
                          italian:
                            'le temperature dell’acqua in ingresso superano 120°F',
                          japanese: '給水温度が120°Fを超えている',
                          portuguese:
                            'as temperaturas da água de entrada excedem 120°F',
                          german:
                            'die Temperaturen des einströmenden Wassers überschreiten 120°F',
                        },
                        details: {
                          english:
                            'Wait for 15 minutes and see if the temperature meets the standard. If not, please use the “I need help” button below to contact Ecolab',
                          spanish:
                            'Espere 15 minutos y vea si la temperatura cumple con el estándar. De lo contrario, utilice el botón "Necesito ayuda" a continuación para comunicarse con Ecolab.',
                          french:
                            'Attendez 15 minutes et vérifiez si la température respecte la norme. Sinon, utilisez le bouton “J’ai besoin d’aide” ci-dessous pour contacter Ecolab.',
                          dutch:
                            'Wacht 15 minuten en controleer of de temperatuur aan de norm voldoet. Zo niet, gebruik dan de knop "Ik heb hulp nodig" hieronder om contact op te nemen met Ecolab',
                          italian:
                            'Attendere 15 minuti e verificare che la temperatura rispetti lo standard. In caso contrario, utilizzare il pulsante “Ho bisogno di aiuto” qui sotto per contattare Ecolab',
                          japanese:
                            '15分待って温度が基準に達しているか確認してください。満たない場合は、下の「助けが必要です」ボタンを使用してエコラボに連絡してください',
                          portuguese:
                            'Aguarde 15 minutos e verifique se a temperatura atende ao padrão. Caso contrário, use o botão “Preciso de ajuda” abaixo para contactar a Ecolab',
                          german:
                            'Warten Sie 15 Minuten und prüfen Sie, ob die Temperatur dem Standard entspricht. Wenn nicht, verwenden Sie bitte die Schaltfläche „Ich brauche Hilfe“, um Ecolab zu kontaktieren',
                        },
                        answered: false,
                      },
                      {
                        title: {
                          english: 'screen is not frozen',
                          spanish: 'la pantalla no está congelada',
                          french: 'L’écran n’est pas gelé',
                          dutch: 'scherm is niet vastgelopen',
                          italian: 'lo schermo non è bloccato',
                          japanese: '画面がフリーズしていない',
                          portuguese: 'a tela não está travada',
                          german: 'Bildschirm ist nicht eingefroren',
                        },
                        details: {
                          english: 'If frozen, unplug and plug back in.',
                          spanish:
                            'Si está congelada, desenchufe y vuelva a enchufar.',
                          french:
                            'En cas de gel, débranchez-le puis rebranchez-le.',
                          dutch:
                            'Als het scherm vastloopt, stekker eruit en weer insteken.',
                          italian:
                            'Se bloccato, scollegare e ricollegare la spina.',
                          japanese:
                            'フリーズしている場合は、電源プラグを抜いてから差し直してください。',
                          portuguese:
                            'Se estiver travada, desligue e ligue novamente da tomada.',
                          german:
                            'Bei eingefrorenem Bildschirm Stecker ziehen und wieder einstecken.',
                        },
                        answered: false,
                      },
                    ],
                  },
                ],
              },
              {
                key: 'elt-not-sure',
                option: 'troubleshooting.notSure',
                label: [
                  'Not Sure',
                  'No estoy seguro(a)',
                  'Pas sûr',
                  'Niet zeker',
                  'Non sono sicuro(a)',
                  'よくわからない',
                  'Não tenho certeza',
                  'Nicht sicher',
                ],
                icon: 'question',
                resolve: false,
                steps: [],
              },
              {
                key: 'elt-no',
                option: 'troubleshooting.no',
                label: [
                  'No',
                  'No',
                  'Non',
                  'Nee',
                  'No',
                  'いいえ',
                  'Não',
                  'Nein',
                ],
                icon: 'cross',
                steps: [
                  {
                    key: 'elt-machinelist-items-no',
                    question: 'Checklist Items for No',
                    options: [],
                    machineIssuelistItems: [
                      {
                        title: {
                          english: 'water is on',
                          spanish: 'el agua esta encendida',
                          french: 'L’eau est allumée',
                          dutch: 'het water staat aan',
                          italian: "l'acqua è aperta",
                          japanese: '水が出ている',
                          portuguese: 'a água está ligada',
                          german: 'Wasser ist eingeschaltet',
                        },
                        details: {
                          english: '',
                          spanish: '',
                          french: '',
                          dutch: '',
                          italian: '',
                          japanese: '',
                          portuguese: '',
                          german: '',
                        },
                        answered: false,
                      },
                      {
                        title: {
                          english: 'drain is shut',
                          spanish: 'el drenaje está cerrado',
                          french: 'Le drain est fermé',
                          dutch: 'afvoer is gesloten',
                          italian: 'lo scarico è chiuso',
                          japanese: 'ドレンが閉まっている',
                          portuguese: 'o dreno está fechado',
                          german: 'Abfluss ist geschlossen',
                        },
                        details: {
                          english: '',
                          spanish: '',
                          french: '',
                          dutch: '',
                          italian: '',
                          japanese: '',
                          portuguese: '',
                          german: '',
                        },
                        answered: false,
                      },
                      {
                        title: {
                          english: 'screen is not frozen',
                          spanish: 'la pantalla no está congelada',
                          french: 'L’écran n’est pas gelé',
                          dutch: 'scherm is niet vastgelopen',
                          italian: 'lo schermo non è bloccato',
                          japanese: '画面がフリーズしていない',
                          portuguese: 'a tela não está travada',
                          german: 'Bildschirm ist nicht eingefroren',
                        },
                        details: {
                          english: 'If frozen, unplug and plug back in.',
                          spanish:
                            'Si está congelada, desenchufe y vuelva a enchufar.',
                          french:
                            'En cas de gel, débranchez-le puis rebranchez-le.',
                          dutch:
                            'Als het scherm vastloopt, stekker eruit en weer insteken.',
                          italian:
                            'Se bloccato, scollegare e ricollegare la spina.',
                          japanese:
                            'フリーズしている場合は、電源プラグを抜いてから差し直してください。',
                          portuguese:
                            'Se estiver travada, desligue e ligue novamente da tomada.',
                          german:
                            'Bei eingefrorenem Bildschirm Stecker ziehen und wieder einstecken.',
                        },
                        answered: false,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'elt-leaking',
        label: [
          'Leaking',
          'Fugas',
          'Fuite',
          'Lekt',
          'Perdita',
          '漏れている',
          'Vazamento',
          'Leckage',
        ],
        option: 'troubleshooting.elt.leaking',
        steps: [
          {
            key: 'elt-leakinglist-items',
            options: [],
            machineIssuelistItems: [
              {
                keyValue: 3,
                title: {
                  english: 'drain stem is not stuck',
                  spanish: 'el vástago del drenaje no está atascado',
                  french: 'Le tube de drain n’est pas bloqué',
                  dutch: 'afvoerpen zit niet vast',
                  italian: 'lo stelo di scarico non è bloccato',
                  japanese: 'ドレンステムが固着していない',
                  portuguese: 'a haste do dreno não está presa',
                  german: 'Ablaufstange klemmt nicht',
                },
                details: {
                  english: '',
                  spanish: '',
                  french: '',
                  dutch: '',
                  italian: '',
                  japanese: '',
                  portuguese: '',
                  german: '',
                  images: [
                    {
                      url: 'assets/images/troubleshooting/elt/elt-leaking-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/elt/elt-leaking-2.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                title: {
                  english: 'end caps in place',
                  spanish: 'tapas finales en su lugar',
                  french: 'Les bouchons d’extrémité en place',
                  dutch: 'eindkappen op hun plaats',
                  italian: 'tappi terminali in posizione',
                  japanese: 'エンドキャップが正しく取り付けられている',
                  portuguese: 'tampas de extremidade no lugar',
                  german: 'Endkappen sind an ihrem Platz',
                },
                details: {
                  english:
                    'May have dropped into the scrap screen or not been replaced after cleaning',
                  spanish:
                    'Es posible que se haya caído al filtro de desechos o que no haya sido reemplazado después de la limpieza.',
                  french:
                    'Peut être tombé dans l’écran à déchets ou n’a pas été remplacé après le nettoyage',
                  dutch:
                    'Kan in het vuilfilter zijn gevallen of niet zijn teruggeplaatst na het schoonmaken',
                  italian:
                    'Potrebbe essere caduto nella griglia dei residui o non essere stato rimesso dopo la pulizia',
                  japanese:
                    '廃棄フィルターに落ちた可能性があるか、清掃後に元に戻されていない可能性があります',
                  portuguese:
                    'Pode ter caído na grelha de resíduos ou não ter sido recolocada após a limpeza',
                  german:
                    'Kann in den Abfallsieb gefallen sein oder wurde nach der Reinigung nicht wieder eingesetzt',
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eht/eht-spray-end-cap.gif',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'elt-cycle-issue',
        label: [
          'Cycle Issue',
          'Problema del ciclo',
          'Problème de cycle',
          'Probleem met cyclus',
          'Problema del ciclo',
          'サイクルの問題',
          'Problema de ciclo',
          'Zyklusproblem',
        ],
        option: 'troubleshooting.elt.cycle',
        steps: [
          {
            key: 'elt-cycleissuelist-items',
            options: [],
            machineIssuelistItems: [
              {
                keyValue: 1,
                title: {
                  english: 'restart the dishmachine',
                  spanish: 'reiniciar el lavavajillas',
                  french: 'Redémarrer le lave-vaisselle',
                  dutch: 'vaatwasmachine opnieuw opstarten',
                  italian: 'riavviare la lavastoviglie',
                  japanese: '食器洗浄機を再起動する',
                  portuguese: 'reinicie a lavadora de louça',
                  german: 'Geschirrspülmaschine neu starten',
                },
                details: {
                  english:
                    'Turn off the machine, wait 30 seconds, then turn it back on',
                  spanish:
                    'Apague la máquina, espere 30 segundos y luego vuelva a encenderla.',
                  french:
                    'Éteignez la machine, attendez 30 secondes, puis rallumez-la',
                  dutch:
                    'Zet de machine uit, wacht 30 seconden en zet hem vervolgens weer aan',
                  italian:
                    'Spegnere la macchina, attendere 30 secondi e poi riaccenderla',
                  japanese:
                    '機械の電源を切り、30秒待ってから再度入れてください',
                  portuguese:
                    'Desligue a máquina, aguarde 30 segundos e ligue novamente',
                  german:
                    'Schalten Sie die Maschine aus, warten Sie 30 Sekunden und schalten Sie sie dann wieder ein',
                  images: [
                    {
                      url: 'assets/images/troubleshooting/elt/elt-cycle-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/elt/elt-cycle-2.png',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'elt-temperature-issue',
        label: [
          'Temperature issue',
          'Problema de temperatura',
          'Problème de température',
          'Temperatuurprobleem',
          'Problema di temperatura',
          '温度の問題',
          'Problema de temperatura',
          'Temperaturproblem',
        ],
        option: 'troubleshooting.elt.temperature',
        steps: [
          {
            key: 'elt-temperature-items',
            options: [],
            machineIssuelistItems: [
              {
                title: {
                  english: 'incoming water temperatures exceed 120°F',
                  spanish:
                    'Las temperaturas del agua entrante superan los 120 °F.',
                  french: 'Les températures d’eau entrantes dépassent 120°F.',
                  dutch:
                    'de temperaturen van het binnenkomende water overschrijden 120°F',
                  italian:
                    'le temperature dell’acqua in ingresso superano 120°F',
                  japanese: '給水温度が120°Fを超えている',
                  portuguese:
                    'as temperaturas da água de entrada excedem 120°F',
                  german:
                    'die Temperaturen des einströmenden Wassers überschreiten 120°F',
                },
                details: {
                  english:
                    'Wait for 15 minutes and see if the temperature meets the standard. If not, please use the “I need help” button below to contact Ecolab',
                  spanish:
                    'Espere 15 minutos y vea si la temperatura cumple con el estándar. De lo contrario, utilice el botón "Necesito ayuda" a continuación para comunicarse con Ecolab.',
                  french:
                    'Attendez 15 minutes et vérifiez si la température respecte la norme. Sinon, utilisez le bouton “J’ai besoin d’aide” ci-dessous pour contacter Ecolab.',
                  dutch:
                    'Wacht 15 minuten en controleer of de temperatuur aan de norm voldoet. Zo niet, gebruik dan de knop "Ik heb hulp nodig" hieronder om contact op te nemen met Ecolab',
                  italian:
                    'Attendere 15 minuti e verificare che la temperatura rispetti lo standard. In caso contrario, utilizzare il pulsante “Ho bisogno di aiuto” qui sotto per contattare Ecolab',
                  japanese:
                    '15分待って温度が基準に達しているか確認してください。満たない場合は、下の「助けが必要です」ボタンを使用してエコラボに連絡してください',
                  portuguese:
                    'Aguarde 15 minutos e verifique se a temperatura atende ao padrão. Caso contrário, use o botão “Preciso de ajuda” abaixo para contactar a Ecolab',
                  german:
                    'Warten Sie 15 Minuten und prüfen Sie, ob die Temperatur dem Standard entspricht. Wenn nicht, verwenden Sie bitte die Schaltfläche „Ich brauche Hilfe“, um Ecolab zu kontaktieren',
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'elt-runs-constantly',
        label: [
          'Runs constantly',
          'Corre constantemente',
          'Fonctionne constamment',
          'Blijft continu draaien',
          'Funziona continuamente',
          '連続運転している',
          'Funciona continuamente',
          'Läuft ständig',
        ],
        option: 'troubleshooting.elt.runs',
        steps: [
          {
            key: 'elt-runsConstantlyIssueList-items',
            options: [],
            machineIssuelistItems: [
              {
                keyValue: 2,
                title: {
                  english: 'restart the dishmachine',
                  spanish: 'reiniciar el lavavajillas',
                  french: 'Redémarrer le lave-vaisselle',
                  dutch: 'vaatwasmachine opnieuw opstarten',
                  italian: 'riavviare la lavastoviglie',
                  japanese: '食器洗浄機を再起動する',
                  portuguese: 'reinicie a lavadora de louça',
                  german: 'Geschirrspülmaschine neu starten',
                },
                details: {
                  english:
                    'Turn off the machine, wait 30 seconds, then turn it back on',
                  spanish:
                    'Apague la máquina, espere 30 segundos y luego vuelva a encenderla.',
                  french:
                    'Éteignez la machine, attendez 30 secondes, puis rallumez-la',
                  dutch:
                    'Zet de machine uit, wacht 30 seconden en zet hem vervolgens weer aan',
                  italian:
                    'Spegnere la macchina, attendere 30 secondi e poi riaccenderla',
                  japanese:
                    '機械の電源を切り、30秒待ってから再度入れてください',
                  portuguese:
                    'Desligue a máquina, aguarde 30 segundos e ligue novamente',
                  german:
                    'Schalten Sie die Maschine aus, warten Sie 30 Sekunden und schalten Sie sie dann wieder ein',
                  images: [
                    {
                      url: 'assets/images/troubleshooting/elt/elt-cycle-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/elt/elt-cycle-2.png',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'elt-door-issue',
        label: [
          'Door issue',
          'Problema con la puerta',
          'Problème de porte',
          'Deurprobleem',
          'Problema con la porta',
          'ドアの問題',
          'Problema na porta',
          'Türproblem',
        ],
        option: 'troubleshooting.elt.door',
        steps: [
          {
            key: 'elt-doorIssueList-items',
            options: [],
            machineIssuelistItems: [
              {
                title: {
                  english: 'door is completely closed',
                  spanish: 'la puerta está completamente cerrada',
                  french: 'la porte est complètement fermée',
                  dutch: 'deur is volledig gesloten',
                  italian: 'la porta è completamente chiusa',
                  japanese: 'ドアが完全に閉まっている',
                  portuguese: 'a porta está completamente fechada',
                  german: 'Tür ist vollständig geschlossen',
                },
                details: {
                  english: '',
                  spanish: '',
                  french: '',
                  dutch: '',
                  italian: '',
                  japanese: '',
                  portuguese: '',
                  german: '',
                },
                answered: false,
              },
              {
                title: {
                  english: 'no loose bolts in the door(s)',
                  spanish: 'No hay pernos sueltos en las puertas.',
                  french: 'Aucune vis desserrée dans la(les) porte(s)',
                  dutch: 'geen losse bouten in de deur(en)',
                  italian: 'nessun bullone allentato nelle porte',
                  japanese: 'ドアに緩んだボルトがない',
                  portuguese: 'não há parafusos soltos na(s) porta(s)',
                  german: 'Keine losen Schrauben an der/den Tür(en)',
                },
                details: {
                  english: '',
                  spanish: '',
                  french: '',
                  dutch: '',
                  italian: '',
                  japanese: '',
                  portuguese: '',
                  german: '',
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'elt-not-draining',
        label: [
          'Not draining',
          'No drenando',
          'Ne pas drainer',
          'Loopt niet leeg',
          'Non scarica',
          '排水しない',
          'Não está drenando',
          'Pumpt nicht ab',
        ],
        option: 'troubleshooting.elt.drain',
        steps: [
          {
            key: 'elt-notDrainingIssueList-items',
            options: [],
            machineIssuelistItems: [
              {
                keyValue: 7,
                title: {
                  english: 'no obstructions are blocking the drain',
                  spanish: 'No hay obstrucciones que bloqueen el drenaje.',
                  french: "Aucun objet n'obstrue le drain.",
                  dutch: 'er zijn geen obstructies die de afvoer blokkeren',
                  italian: 'nessuna ostruzione blocca lo scarico',
                  japanese: '排水が何かで塞がれていない',
                  portuguese: 'não há obstruções bloqueando o dreno',
                  german: 'Keine Hindernisse blockieren den Abfluss',
                },
                details: {
                  english:
                    "If it's completely full, please use the “I need help” button below to contact Ecolab",
                  spanish:
                    'Si está completamente lleno, utilice el botón "Necesito ayuda" a continuación para comunicarse con Ecolab.',
                  french:
                    "S'il est complètement plein, veuillez utiliser le bouton “J’ai besoin d’aide” ci-dessous pour contacter Ecolab",
                  dutch:
                    'Als hij helemaal vol is, gebruik dan de knop "Ik heb hulp nodig" hieronder om contact op te nemen met Ecolab',
                  italian:
                    'Se è completamente pieno, utilizzare il pulsante “Ho bisogno di aiuto” qui sotto per contattare Ecolab',
                  japanese:
                    '完全に満杯の場合は、下の「助けが必要です」ボタンを使用してエコラボに連絡してください',
                  portuguese:
                    'Se estiver completamente cheio, utilize o botão “Preciso de ajuda” abaixo para contactar a Ecolab',
                  german:
                    'Wenn er komplett voll ist, verwenden Sie bitte die Schaltfläche „Ich brauche Hilfe“, um Ecolab zu kontaktieren',
                  images: [
                    {
                      url: 'assets/images/troubleshooting/elt/elt-leaking-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/elt/elt-leaking-2.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/elt/elt-not-drain-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eht/eht-not-drain-3.png',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'elt-making-noise',
        label: [
          'Making noise',
          'Haciendo ruido',
          'Faire du bruit',
          'Maakt lawaai',
          'Fa rumore',
          '異音がする',
          'Faz barulho',
          'Macht Geräusche',
        ],
        option: 'troubleshooting.elt.noise',
        steps: [
          {
            key: 'elt-noiseIssueList-items',
            options: [],
            machineIssuelistItems: [
              {
                title: {
                  english: 'nothing is stuck in the wash tank',
                  spanish: 'no hay nada atascado en el tanque de lavado',
                  french: 'Rien n’est coincé dans le réservoir de lavage',
                  dutch: 'er zit niets vast in de wastank',
                  italian: 'non c’è nulla incastrato nel serbatoio di lavaggio',
                  japanese: '洗浄タンクに物が詰まっていない',
                  portuguese: 'nada está preso no tanque de lavagem',
                  german: 'Nichts steckt im Waschtank fest',
                },
                details: {
                  english: '',
                  spanish: '',
                  french: '',
                  dutch: '',
                  italian: '',
                  japanese: '',
                  portuguese: '',
                  german: '',
                },
                answered: false,
              },
              {
                title: {
                  english: 'water in the tank',
                  spanish: 'agua en el tanque',
                  french: 'Eau dans le réservoir',
                  dutch: 'water in de tank',
                  italian: 'acqua nel serbatoio',
                  japanese: 'タンク内に水がある',
                  portuguese: 'água no tanque',
                  german: 'Wasser im Tank',
                },
                details: {
                  english: '',
                  spanish: '',
                  french: '',
                  dutch: '',
                  italian: '',
                  japanese: '',
                  portuguese: '',
                  german: '',
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'elt-spray-arm',
        label: [
          'Spray arm',
          'Brazo rociador',
          'Bras de pulvérisation',
          'Sproeiarm',
          'Braccio irroratore',
          'スプレーアーム',
          'Braço pulverizador',
          'Sprüharm',
        ],
        option: 'troubleshooting.elt.sprayArm',
        steps: [
          {
            key: 'elt-sprayIssueList-items',
            options: [],
            machineIssuelistItems: [
              {
                title: {
                  english: 'nozzles not clogged',
                  spanish: 'boquillas no obstruidas',
                  french: 'Les buses ne sont pas obstruées',
                  dutch: 'sproeiers niet verstopt',
                  italian: 'ugelli non ostruiti',
                  japanese: 'ノズルが詰まっていない',
                  portuguese: 'bicos não obstruídos',
                  german: 'Düsen nicht verstopft',
                },
                details: {
                  english: 'Remove end caps and rinse out',
                  spanish: 'Retire las tapas de los extremos y enjuáguelas.',
                  french: 'Retirez les bouchons de fin et rincez-les.',
                  dutch: 'Verwijder de eindkappen en spoel ze uit',
                  italian: 'Rimuovere i tappi terminali e risciacquare',
                  japanese: 'エンドキャップを外してすすいでください',
                  portuguese: 'Remova as tampas de extremidade e enxágue',
                  german: 'Endkappen entfernen und ausspülen',
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eht/eht-spray-nozzle-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/eht/eht-spray-nozzle-2.gif',
                    },
                  ],
                },
                answered: false,
              },
              {
                title: {
                  english: 'end caps in place',
                  spanish: 'tapas finales en su lugar',
                  french: 'Les bouchons d’extrémité en place',
                  dutch: 'eindkappen op hun plaats',
                  italian: 'tappi terminali in posizione',
                  japanese: 'エンドキャップが正しく取り付けられている',
                  portuguese: 'tampas de extremidade no lugar',
                  german: 'Endkappen sind an ihrem Platz',
                },
                details: {
                  english:
                    'May have dropped into the scrap screen or not been replaced after cleaning',
                  spanish:
                    'Es posible que se haya caído al filtro de desechos o que no haya sido reemplazado después de la limpieza.',
                  french:
                    'Peut être tombé dans l’écran à déchets ou n’a pas été remplacé après le nettoyage',
                  dutch:
                    'Kan in het vuilfilter zijn gevallen of niet zijn teruggeplaatst na het schoonmaken',
                  italian:
                    'Potrebbe essere caduto nella griglia dei residui o non essere stato rimesso dopo la pulizia',
                  japanese:
                    '廃棄フィルターに落ちた可能性があるか、清掃後に元に戻されていない可能性があります',
                  portuguese:
                    'Pode ter caído na grelha de resíduos ou não ter sido recolocada após a limpeza',
                  german:
                    'Kann in den Abfallsieb gefallen sein oder wurde nach der Reinigung nicht wieder eingesetzt',
                  images: [
                    {
                      url: 'assets/images/troubleshooting/elt/elt-endcap-1.png',
                    },
                    {
                      url: 'assets/images/troubleshooting/elt/elt-endcap-2.png',
                    },
                  ],
                },
                answered: false,
              },
              {
                title: {
                  english: 'arm spins freely',
                  spanish: 'el brazo gira libremente',
                  french: 'Le bras tourne librement',
                  dutch: 'arm draait vrij',
                  italian: 'il braccio gira liberamente',
                  japanese: 'アームが自由に回転する',
                  portuguese: 'o braço gira livremente',
                  german: 'der Arm dreht sich frei',
                },
                details: {
                  english: 'Spin spray arm with your hand ',
                  spanish: 'Gire el brazo rociador con la mano',
                  french:
                    'Faites tourner le bras de pulvérisation avec votre main',
                  dutch: 'Draai de sproeiarm met de hand',
                  italian: 'Fai girare il braccio irroratore con la mano',
                  japanese: '手でスプレーアームを回してください',
                  portuguese: 'Gire o braço pulverizador com a mão',
                  german: 'Drehen Sie den Sprüharm mit der Hand',
                  images: [
                    {
                      url: 'assets/images/troubleshooting/eht/eht-spray-arm-spins.gif',
                    },
                  ],
                },
                answered: false,
              },
            ],
          },
        ],
      },
      {
        key: 'elt-other',
        label: [
          'Other',
          'Otro',
          'Autre',
          'Overig',
          'Altro',
          'その他',
          'Outro',
          'Sonstiges',
        ],
        option: 'troubleshooting.elt.other',
      },
    ],
  };
}
